import { Injectable } from '@angular/core';
import { ConfirmationService } from 'primeng/api';
import Sample from '../../../../types/sample/Sample';
import { SampleState } from '../../../../types/sample/SampleState';
import { SampleService } from '../sample.service';

@Injectable({
    providedIn: 'root',
})
export class SampleHelperService {
    constructor(
        private confirmationService: ConfirmationService,
        private sampleService: SampleService
    ) {
    }

    /**
     * Deletes a sample.
     *
     * @param sample The sample to delete.
     * @param reloadCallback The callback to reload the samples after deletion.
     * @return void
     */
    public deleteSample(sample: Sample, reloadCallback: () => void): void {
        this.confirmationService.confirm({
            header: 'Delete sample',
            message: 'Are you sure you want to delete this sample? This action cannot be undone.',
            accept: () => {
                this.sampleService
                    .deleteSample(sample.id)
                    .then(reloadCallback)
                    .catch((error: any) => console.error('Error deleting sample:', error));
            },
        });
    }

    /**
     * Determines whether the publish sample button should be shown.
     *
     * @param sample The sample to check.
     * @return Whether the publish sample button should be shown.
     */
    public showPublishSampleButton(sample: Sample): boolean {
        return sample.state === SampleState.CREATED;
    }

    /**
     * Determines whether the request approval button should be shown.
     *
     * @param sample The sample to check.
     * @return Whether the request approval button should be shown.
     */
    public showRequestApprovalButton(sample: Sample): boolean {
        return sample.state === SampleState.PUBLISHED || sample.state === SampleState.CHANGE_REQUESTED;
    }

    /**
     * Determines whether the approval sample button should be shown.
     *
     * @param sample The sample to check.
     * @return Whether the approval button should be shown.
     */
    public showApproveSampleByReviewerButton(sample: Sample): boolean {
        return sample.state === SampleState.APPROVAL_REQUESTED;
    }

    /**
     * Determines whether the request changes button should be shown.
     *
     * @param sample The sample to check.
     * @return Whether the request changes button should be shown.
     */
    public showRequestChangesButton(sample: Sample): boolean {
        return sample.state === SampleState.APPROVAL_REQUESTED;
    }

    /**
     * Determines whether the approval sample button should be shown.
     *
     * @param sample The sample to check.
     * @return Whether the approval sample button should be shown.
     */
    public showApproveSampleByProcessOwnerButton(sample: Sample): boolean {
        return sample.state === SampleState.APPROVED_BY_REVIEWER || sample.state === SampleState.IN_PRODUCTION;
    }

    /**
     * Determines whether the set in production button should be shown.
     *
     * @param sample The sample to check.
     * @return Whether the set in production button should be shown.
     */
    public showSetInProductionButton(sample: Sample): boolean {
        return sample.state === SampleState.APPROVED_BY_REVIEWER;
    }

    /**
     * Determines whether the set active button should be shown to a producer.
     * Producers may only set a sample active once it is in production; the process owner
     * keeps the shortcut from APPROVED_BY_REVIEWER directly to ACTIVE.
     *
     * @param sample The sample to check.
     * @return Whether the set active button should be shown to a producer.
     */
    public showSetActiveByProducerButton(sample: Sample): boolean {
        return sample.state === SampleState.IN_PRODUCTION;
    }

    /**
     * Determines whether the archive sample button should be shown.
     *
     * @param sample The sample to check.
     * @return Whether the archive sample button should be shown.
     */
    public showArchiveSampleButton(sample: Sample): boolean {
        return sample.state === SampleState.ACTIVE;
    }

    /**
     * Determines whether the request master sample button should be shown.
     *
     * @param sample The sample to check.
     * @return Whether the request master sample button should be shown.
     */
    public showRequestMasterSampleButton(sample: Sample) {
        return sample.state === SampleState.PUBLISHED;
    }
}
